import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F7FB]">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/14036444/pexels-photo-14036444.jpeg"
          alt="Travel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="overline text-white/80 mb-3">Travelhostic Travel CRM</div>
          <h1 className="font-display text-5xl leading-tight max-w-md">
            Build memorable journeys, one email at a time.
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div>
            <div className="overline mb-3">Get started</div>
            <div className="flex items-center gap-3 mb-2">
              <img src="/travelhostic-mark.png" alt="Travelhostic" className="w-12 h-12 object-contain" />
              <h2 className="font-display text-4xl text-[#0F172A]">Create account</h2>
            </div>
            <p className="text-[#64748B] mt-2 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-[#1E3A8A] underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password-input" />
            </div>
          </div>

          {error && (
            <div className="text-sm text-[#DC2626] bg-[#DC2626]/10 px-3 py-2 rounded-lg" data-testid="register-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E3A8A] hover:bg-[#172A6B] text-white"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
